import { useContext, useMemo } from 'react';
import { StreamsContext } from '../contexts/StreamsContext';
import { AppStateContext } from '../contexts/AppStateContext';

function barTimeMs(bar) {
  if (bar?.Epoch != null) {
    const epochMs = Number(bar.Epoch);
    if (Number.isFinite(epochMs) && epochMs > 0) return epochMs;
  }
  const timestamp = bar?.TimeStamp || bar?.TradeTime;
  if (timestamp == null || timestamp === '') return NaN;
  if (typeof timestamp === 'number') return timestamp;
  const parsed = Date.parse(timestamp);
  return Number.isFinite(parsed) ? parsed : NaN;
}

/** Stream bars are already chronological. Sort only when a merge left them out of order. */
function barsInTimeOrder(list) {
  const times = new Array(list.length);
  let ordered = true;
  let prev = -Infinity;
  for (let i = 0; i < list.length; i += 1) {
    const time = barTimeMs(list[i]);
    times[i] = time;
    if (!(time >= prev)) ordered = false;
    else prev = time;
  }
  if (ordered) return list;
  return list
    .map((bar, index) => ({ bar, time: times[index] }))
    .sort((a, b) => a.time - b.time)
    .map((entry) => entry.bar);
}

// Compute simple ATR(N) from shared currentBars; returns null if insufficient data
// If interval/unit/ticker are provided, uses those; otherwise falls back to global AppStateContext values
export const useAtr = (period = 100, multiplier = 1.0, overrides = {}) => {
  const { barsByKey } = useContext(StreamsContext);
  const { 
    chartInterval: globalInterval, 
    chartUnit: globalUnit, 
    sessionTemplate: globalSessionTemplate, 
    selectedTicker: globalSelectedTicker,
    charts,
    activeChartId
  } = useContext(AppStateContext);
  
  // If overrides not provided, try to get from targeted chart, then fall back to global
  const targetedChart = useMemo(() => {
    if (!activeChartId || !Array.isArray(charts)) return null;
    return charts.find(c => c.id === activeChartId);
  }, [charts, activeChartId]);
  
  // CRITICAL: Include targeted chart's interval in dependencies to recompute when it changes
  const ticker = overrides.ticker || targetedChart?.ticker || globalSelectedTicker;
  const interval = overrides.interval || targetedChart?.interval || globalInterval;
  const unit = overrides.unit || targetedChart?.unit || globalUnit;
  const sessionTemplate = overrides.sessionTemplate || globalSessionTemplate;

  const bars = useMemo(() => {
    const key = [ticker, interval, unit, sessionTemplate || 'Default'].join('|');
    const keyed = barsByKey?.[key];

    if (Array.isArray(keyed) && keyed.length > 0) return keyed;
    
    return [];
  }, [barsByKey, ticker, interval, unit, sessionTemplate]);

  const atr = useMemo(() => {
    const list = Array.isArray(bars) ? bars : [];
    if (list.length < 2) {
      return null; // Need at least 2 bars to calculate True Range
    }
    const sorted = barsInTimeOrder(list);
    const trs = [];
    for (let i = 1; i < sorted.length; i++) {
      const h = parseFloat(sorted[i]?.High);
      const l = parseFloat(sorted[i]?.Low);
      const cPrev = parseFloat(sorted[i - 1]?.Close);
      if (!isFinite(h) || !isFinite(l) || !isFinite(cPrev)) continue;
      const hl = h - l;
      const hcp = Math.abs(h - cPrev);
      const lcp = Math.abs(l - cPrev);
      const tr = Math.max(hl, hcp, lcp);
      if (isFinite(tr)) trs.push(tr);
    }
    if (trs.length === 0) {
      return null; // No valid True Ranges calculated
    }
    // Use up to 'period' periods (upper limit), but work with any number available
    // This allows ATR calculation even with fewer bars (e.g., monthly charts with only 12 bars)
    const actualPeriod = Math.min(period, trs.length);
    const recent = trs.slice(-actualPeriod);
    const mean = recent.reduce((s, v) => s + v, 0) / actualPeriod;
    const result = multiplier * mean;
    return result;
  }, [bars, period, multiplier]);

  return atr;
}

export default useAtr;


